import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Form, Select, Button, Tooltip, Modal, InputNumber, message, Space, Popconfirm, Alert, Progress } from 'antd';
import moment from 'moment';
import {
  getExcelCombinedV2, updateExcelCombinedRowV2, deleteCombinedRowV2, getEmployees,
  generatePayroll, updatePayroll, downloadSalarySlip, previewSalarySlip,
  initZipPayroll, addSlipToZipPayroll, finalizeZipPayroll, sendPayslipWatiWhatsapp
} from '../api';
import { EditOutlined, DeleteOutlined, EyeOutlined, FilePdfOutlined, FileZipOutlined, WhatsAppOutlined } from '@ant-design/icons';

const months = Array.from({ length: 12 }, (_, i) => ({ label: moment().month(i).format('MMMM'), value: i + 1 }));
const years = Array.from({ length: 8 }, (_, i) => ({ label: String(moment().year() - 3 + i), value: moment().year() - 3 + i }));

const PayrollReport = () => {
  const now = moment();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ month: now.month() + 1, year: now.year() });
  const [form] = Form.useForm();
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [slipGeneratingId, setSlipGeneratingId] = useState(null);
  const [zipProgress, setZipProgress] = useState({ visible: false, current: 0, total: 0, status: '' });
  const [watiProgress, setWatiProgress] = useState({ visible: false, current: 0, total: 0, status: '', errors: [] });
  const [watiConfirm, setWatiConfirm] = useState({ visible: false, missingCount: 0, validCount: 0, missingList: [], validRows: [] });
  const daysInMonth = useMemo(() => moment({ year: filters.year, month: filters.month - 1 }).daysInMonth(), [filters]);

  const fetchData = async (filterValues = filters) => {
    setLoading(true);
    try {
      const res = await getExcelCombinedV2(filterValues);
      const data = res.data || {};
      const cleaned = (data.rows || []).filter(r => (r.name || '').trim() !== '' && (r.company_subcontractor || '').trim() !== '');
      const finalRows = cleaned.filter(r => String(r.empcode || '').trim() !== '');
      setRows(finalRows);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);
  useEffect(() => {
    (async () => {
      const res = await getEmployees();
      if (res.success) setEmployees(res.data || []);
    })();
  }, []);

  const handleFilterChange = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    fetchData(next);
  };

  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const formatNum = (v) => {
    return num(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const empMap = useMemo(() => {
    const map = {};
    employees.forEach(e => {
      const c = String(e.emp_code || '').trim();
      if (c) map[c] = e;
    });
    return map;
  }, [employees]);

  const missingEmployees = useMemo(() => {
    return rows.filter(r => {
      const code = String(r.empcode || '').trim();
      return code && !empMap[code];
    });
  }, [rows, empMap]);

  const columns = useMemo(() => [
    {
      title: 'Action', key: 'action', width: 140, fixed: 'left', render: (_, r) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => { setEditRow(r); setEditOpen(true); }} />
          </Tooltip>
          <Tooltip title="Preview Salary Slip">
            <Button size="small" icon={<EyeOutlined />} loading={slipGeneratingId === `preview-${r.upload_id}-${r.empcode}-${r.row_index}`} onClick={async () => {
              try {
                setSlipGeneratingId(`preview-${r.upload_id}-${r.empcode}-${r.row_index}`);
                const empCode = String(r.empcode || '').trim();
                const emp = empMap[empCode];
                if (!emp) {
                  message.error('Employee not found for salary slip');
                  setSlipGeneratingId(null);
                  return;
                }
                const m = String(filters.month).padStart(2, '0');
                const month_year = `${filters.year}-${m}`;
                const isMonthly = emp.salary_type === 'monthly';
                const payload = {
                  employee_id: emp.id,
                  month_year,
                  hours_worked: isMonthly ? 1 : num(r.total),
                  hourly_rate: num(r.rate),
                  site_allowance: num(r.allowance),
                  other_allowance: 0,
                  housekeeping: num(r.cleaning),
                  other_deductions: num(r.other_deduction),
                  loan_deduction: num(r.loan_deduction),
                };
                const gen = await generatePayroll(payload);
                if (!gen.success) {
                  message.error(gen.message || 'Failed to generate payroll');
                  setSlipGeneratingId(null);
                  return;
                }
                await updatePayroll(gen.data.id, payload);
                await previewSalarySlip(gen.data.id);
              } catch (err) {
                message.error(err.message || 'Failed to preview salary slip');
              } finally {
                setSlipGeneratingId(null);
              }
            }} />
          </Tooltip>
          <Tooltip title="Download Salary Slip">
            <Button size="small" icon={<FilePdfOutlined />} loading={slipGeneratingId === `${r.upload_id}-${r.empcode}-${r.row_index}`} onClick={async () => {
              try {
                setSlipGeneratingId(`${r.upload_id}-${r.empcode}-${r.row_index}`);
                const empCode = String(r.empcode || '').trim();
                const emp = empMap[empCode];
                if (!emp) {
                  message.error('Employee not found for salary slip');
                  setSlipGeneratingId(null);
                  return;
                }
                const m = String(filters.month).padStart(2, '0');
                const month_year = `${filters.year}-${m}`;
                const isMonthly = emp.salary_type === 'monthly';
                const payload = {
                  employee_id: emp.id,
                  month_year,
                  hours_worked: isMonthly ? 1 : num(r.total),
                  hourly_rate: num(r.rate),
                  site_allowance: num(r.allowance),
                  other_allowance: 0,
                  housekeeping: num(r.cleaning),
                  other_deductions: num(r.other_deduction),
                  loan_deduction: num(r.loan_deduction),
                };
                const gen = await generatePayroll(payload);
                if (!gen.success) {
                  message.error(gen.message || 'Failed to generate payroll');
                  setSlipGeneratingId(null);
                  return;
                }
                await updatePayroll(gen.data.id, payload);
                await downloadSalarySlip(gen.data.id);
                message.success('Salary slip downloaded');
              } catch (err) {
                message.error(err.message || 'Failed to download salary slip');
              } finally {
                setSlipGeneratingId(null);
              }
            }} />
          </Tooltip>
          <Popconfirm
            title="Delete this row?"
            onConfirm={async () => {
              setLoading(true);
              try {
                const res = await deleteCombinedRowV2({ upload_id: r.upload_id, row_index: r.row_index });
                if (res.success) {
                  message.success(res.message);
                  fetchData();
                } else {
                  message.error(res.message);
                }
              } catch (e) {
                message.error('Failed to delete row');
              } finally {
                setLoading(false);
              }
            }}
          >
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    },
    {
      title: 'Status', key: 'record_status', width: 100, fixed: 'left',
      render: (_, r) => {
        const code = String(r.empcode || '').trim();
        return code && empMap[code] ? <span style={{ color: '#52c41a' }}>Ready</span> : <span style={{ color: '#f5222d' }}>Missing</span>;
      }
    },
    { title: 'Empcode', dataIndex: 'empcode', width: 120, fixed: 'left' },
    { title: 'Name', dataIndex: 'name', width: 220, fixed: 'left' },
    { title: 'Basic Salary', dataIndex: 'basic_salary', width: 140, render: (v) => formatNum(v) },
    { title: 'Rate', dataIndex: 'rate', width: 120, render: (v) => formatNum(v) },
    { title: 'Hours Worked', dataIndex: 'total', width: 140, render: (v) => formatNum(v) },
    {
      title: 'Allowance', dataIndex: 'allowance', width: 140, render: (v, r) => {
        return formatNum(v);
      }
    },
    { title: 'Loan', dataIndex: 'loan', width: 140, render: (v) => formatNum(v) },
    { title: 'Loan Deduction', dataIndex: 'loan_deduction', width: 160, render: (v) => formatNum(v) },
    { title: 'Remaining Balance', dataIndex: 'remaining_balance', width: 160, render: (v) => formatNum(v) },
    { title: 'Other Deduction', dataIndex: 'other_deduction', width: 160, render: (v) => formatNum(v) },
    { title: 'Cleaning', dataIndex: 'cleaning', width: 140, render: (v) => formatNum(v) },
    {
      title: 'Deductions Total', key: 'deductions_total', width: 160, render: (_, r) => {
        const d = num(r.loan_deduction) + num(r.other_deduction) + num(r.cleaning);
        return formatNum(d);
      }
    },
    {
      title: 'Grand Total', key: 'grand_total', width: 160, render: (_, r) => {
        const empCode = String(r.empcode || '').trim();
        const emp = empMap[empCode];
        const isMonthly = emp?.salary_type === 'monthly';

        const hoursTotal = num(r.total);
        const rate = num(r.rate);

        let base;
        if (isMonthly) {
          base = rate;
        } else {
          base = hoursTotal * rate;
        }

        const allowance = num(r.allowance);
        const basicSalary = num(r.basic_salary);
        const grossPreMin = base + allowance;
        const grandTotal = basicSalary > 0 ? Math.max(grossPreMin, basicSalary) : grossPreMin;
        return formatNum(grandTotal);
      }
    },
    {
      title: 'Net Total', key: 'net_total', width: 160, render: (_, r) => {
        const empCode = String(r.empcode || '').trim();
        const emp = empMap[empCode];
        const isMonthly = emp?.salary_type === 'monthly';

        const hoursTotal = num(r.total);
        const rate = num(r.rate);

        let base;
        if (isMonthly) {
          base = rate;
        } else {
          base = hoursTotal * rate;
        }

        const allowance = num(r.allowance);
        const basicSalary = num(r.basic_salary);
        const grossPreMin = base + allowance;
        const grandTotal = basicSalary > 0 ? Math.max(grossPreMin, basicSalary) : grossPreMin;
        const ded = num(r.loan_deduction) + num(r.other_deduction) + num(r.cleaning);
        const net = grandTotal - ded;
        return formatNum(net);
      }
    },
  ], [employees, slipGeneratingId, filters, empMap]);

  const handleDownloadAllZip = async () => {
    // Only employees that exist in Master list can have payslips
    const readyRows = rows.filter(r => {
      const code = String(r.empcode || '').trim();
      return code && empMap[code];
    });

    if (readyRows.length === 0) {
      message.warning('No ready employee records found to generate payslips.');
      return;
    }

    try {
      setZipProgress({ visible: true, current: 0, total: readyRows.length, status: 'Initializing ZIP session...' });

      const initRes = await initZipPayroll();
      if (!initRes.success) throw new Error('Failed to start ZIP session');
      const zipId = initRes.data.zip_id;

      const m = String(filters.month).padStart(2, '0');
      const month_year = `${filters.year}-${m}`;

      for (let i = 0; i < readyRows.length; i++) {
        const r = readyRows[i];
        const empCode = String(r.empcode || '').trim();
        const emp = empMap[empCode];
        const isMonthly = emp.salary_type === 'monthly';

        setZipProgress(prev => ({ ...prev, current: i + 1, status: `Generating payslip for ${r.name}...` }));

        const payload = {
          zip_id: zipId,
          employee_id: emp.id,
          month_year,
          hours_worked: isMonthly ? 1 : num(r.total),
          hourly_rate: num(r.rate),
          site_allowance: num(r.allowance),
          other_allowance: 0,
          housekeeping: num(r.cleaning),
          other_deductions: num(r.other_deduction),
          loan_deduction: num(r.loan_deduction),
        };

        const addRes = await addSlipToZipPayroll(payload);
        if (!addRes.success) {
          console.warn(`Failed for ${r.name}: ${addRes.message}`);
        }
      }

      setZipProgress(prev => ({ ...prev, status: 'Finalizing ZIP file...' }));
      await finalizeZipPayroll(zipId, `Payslips_${monthName}_${filters.year}.zip`);

      message.success('Bulk download complete!');
    } catch (err) {
      console.error(err);
      message.error(err.message || 'Failed to generate bulk ZIP');
    } finally {
      setZipProgress({ visible: false, current: 0, total: 0, status: '' });
    }
  };

  const runBroadcast = async (validRows) => {
    setWatiProgress({ visible: true, current: 0, total: validRows.length, status: 'Starting broadcast...', errors: [] });
    const month_year = `${filters.year}-${String(filters.month).padStart(2, '0')}`;
    let errs = [];

    for (let i = 0; i < validRows.length; i++) {
      const r = validRows[i];
      const emp = empMap[String(r.empcode || '').trim()];
      const isMonthly = emp.salary_type === 'monthly';

      setWatiProgress(p => ({ ...p, current: i + 1, status: `Sending to ${r.name}...` }));
      
      try {
        const res = await sendPayslipWatiWhatsapp({
          employee_id: emp.id,
          month_year,
          hours_worked: isMonthly ? 1 : num(r.total),
          hourly_rate: num(r.rate),
          site_allowance: num(r.allowance),
          other_allowance: 0,
          housekeeping: num(r.cleaning),
          other_deductions: num(r.other_deduction),
          loan_deduction: num(r.loan_deduction),
        });
        if (!res.data.success) errs.push(`${r.name}: ${res.data.message}`);
      } catch (e) {
        errs.push(`${r.name}: ${e.response?.data?.message || e.message}`);
      }
    }

    setWatiProgress(p => ({ ...p, status: 'Done.', errors: errs }));
    if (errs.length > 0) message.warning(`Finished with ${errs.length} errors.`);
    else {
      message.success('All sent!');
      setTimeout(() => setWatiProgress({ visible: false, current: 0, total: 0, status: '', errors: [] }), 2000);
    }
  };

  const handleSendAllWati = () => {
    const readyRows = rows.filter(r => {
      const code = String(r.empcode || '').trim();
      return code && empMap[code];
    });

    if (readyRows.length === 0) {
      message.warning('No ready employee records found.');
      return;
    }

    const missingPhones = [];
    const validRows = [];

    readyRows.forEach(r => {
      const empCode = String(r.empcode || '').trim();
      const emp = empMap[empCode];
      if (!emp?.phone || String(emp.phone).trim() === '') {
        missingPhones.push(r.name);
      } else {
        validRows.push(r);
      }
    });

    if (validRows.length === 0) {
      message.error('No employees have valid phone numbers.');
      return;
    }

    if (missingPhones.length > 0) {
      setWatiConfirm({
        visible: true,
        missingCount: missingPhones.length,
        validCount: validRows.length,
        missingList: missingPhones,
        validRows: validRows
      });
    } else {
      runBroadcast(validRows).catch(console.error);
    }
  };

  const monthName = moment().month(filters.month - 1).format('MMMM');

  return (
    <div>
      <Card
        title={
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 12 }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
              PAYROLL REPORT ({monthName.toUpperCase()} {filters.year})
            </div>
            {missingEmployees.length > 0 && (
              <Alert
                message="Master Employee Records Missing"
                description={`${missingEmployees.length} employees found in this report are not registered in the Master Employee list. You will not be able to generate payslips for them until they are synchronized.`}
                type="error"
                showIcon
                closable
              />
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 16 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Form.Item label="Month" style={{ margin: 0 }}>
                  <Select
                    style={{ width: 120 }}
                    value={filters.month}
                    options={months}
                    onChange={(v) => handleFilterChange('month', v)}
                  />
                </Form.Item>
                <Form.Item label="Year" style={{ margin: 0 }}>
                  <Select
                    style={{ width: 100 }}
                    value={filters.year}
                    options={years}
                    onChange={(v) => handleFilterChange('year', v)}
                  />
                </Form.Item>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  type="primary"
                  icon={<WhatsAppOutlined />}
                  onClick={handleSendAllWati}
                  disabled={rows.length === 0}
                  style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
                >
                  Send All (WhatsApp)
                </Button>
                <Button
                  type="primary"
                  icon={<FileZipOutlined />}
                  onClick={handleDownloadAllZip}
                  disabled={rows.length === 0}
                >
                  Download All (ZIP)
                </Button>
              </div>
            </div>
          </div>
        }
      >
        <Modal
          title="Generating Bulk Payslips"
          open={zipProgress.visible}
          footer={null}
          closable={false}
          maskClosable={false}
        >
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Progress
              type="circle"
              percent={Math.round((zipProgress.current / zipProgress.total) * 100)}
              status="active"
            />
            <div style={{ marginTop: 20 }}>
              <p><b>{zipProgress.status}</b></p>
              <p>{zipProgress.current} of {zipProgress.total} processed</p>
            </div>
          </div>
        </Modal>
        <Modal
          title="Sending Payslips via WhatsApp (Wati)"
          open={watiProgress.visible}
          footer={watiProgress.errors.length > 0 ? <Button onClick={() => setWatiProgress({ visible: false, current: 0, total: 0, status: '', errors: [] })}>Close</Button> : null}
          closable={watiProgress.errors.length > 0}
          maskClosable={false}
        >
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Progress
              type="circle"
              percent={watiProgress.total > 0 ? Math.round((watiProgress.current / watiProgress.total) * 100) : 0}
              status={watiProgress.errors.length > 0 ? "exception" : "active"}
              strokeColor={{ '0%': '#25D366', '100%': '#128C7E' }}
            />
            <div style={{ marginTop: 20 }}>
              <p><b>{watiProgress.status}</b></p>
              <p>{watiProgress.current} of {watiProgress.total} processed</p>
            </div>
            {watiProgress.errors.length > 0 && (
              <div style={{ marginTop: 20, textAlign: 'left', maxHeight: '150px', overflowY: 'auto' }}>
                <Alert
                  type="error"
                  message="Errors Encountered"
                  description={
                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                      {watiProgress.errors.map((e, idx) => <li key={idx} style={{ fontSize: '12px' }}>{e}</li>)}
                    </ul>
                  }
                />
              </div>
            )}
          </div>
        </Modal>
        <Modal
          title="Missing Phone Numbers"
          open={watiConfirm.visible}
          onOk={() => {
            setWatiConfirm(p => ({ ...p, visible: false }));
            runBroadcast(watiConfirm.validRows).catch(console.error);
          }}
          onCancel={() => setWatiConfirm(p => ({ ...p, visible: false }))}
          okText="Yes, Send Now"
          cancelText="Cancel"
          width={600}
        >
          <div>
            <p>We found <b>{watiConfirm.missingCount}</b> employees without phone numbers. Only <b>{watiConfirm.validCount}</b> have phone numbers registered.</p>
            <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #eee', padding: 12, borderRadius: 4, background: '#fafafa' }}>
               <ul style={{ paddingLeft: 20, margin: 0 }}>
                 {watiConfirm.missingList.map((n, idx) => <li key={idx} style={{ fontSize: '12px' }}>{n}</li>)}
               </ul>
            </div>
            <p style={{ marginTop: 16 }}>Do you want to proceed with sending to the valid <b>{watiConfirm.validCount}</b> employees?</p>
          </div>
        </Modal>
        <Modal
          open={editOpen}
          title="Edit Row"
          onCancel={() => { setEditOpen(false); setEditRow(null); }}
          onOk={async () => {
            if (!editRow) return;
            try {
              setEditSaving(true);
              const days = {};
              for (let d = 1; d <= daysInMonth; d++) {
                const key = `day_${d}`;
                days[key] = editRow[key] ?? null;
              }
              await updateExcelCombinedRowV2({ upload_id: editRow.upload_id, row_index: editRow.row_index, empcode: editRow.empcode, name: editRow.name, rate: editRow.rate ?? null, days });
              setEditOpen(false);
              setEditRow(null);
              fetchData(filters);
              message.success('Row updated');
            } finally {
              setEditSaving(false);
            }
          }}
          confirmLoading={editSaving}
          width={800}
        >
          {editRow && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                <div>
                  Rate
                  <InputNumber style={{ width: '100%' }} min={0} step={0.5} value={editRow.rate ?? null} onChange={(v) => setEditRow({ ...editRow, rate: v })} />
                </div>
                <div>
                  Total
                  <InputNumber style={{ width: '100%' }} min={0} step={0.5} value={editRow.total ?? null} disabled />
                </div>
              </div>
              <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 }}>
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const d = i + 1; const key = `day_${d}`;
                  return (
                    <div key={key}>
                      {`Day ${d}`}
                      <InputNumber style={{ width: '100%' }} min={0} step={0.5} value={editRow[key] ?? null} onChange={(v) => {
                        const next = { ...editRow, [key]: v };
                        let sum = 0;
                        for (let j = 1; j <= daysInMonth; j++) { const k = `day_${j}`; sum += Number(next[k] || 0); }
                        next.total = sum;
                        setEditRow(next);
                      }} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Modal>
        <Table
          rowKey={(r) => `${r.upload_id}-${r.empcode}-${r.row_index}`}
          loading={loading}
          dataSource={rows}
          columns={columns}
          size="small"
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 200 }}
          rowClassName={(r) => {
            const code = String(r.empcode || '').trim();
            return (code && !empMap[code]) ? 'missing-record-row' : '';
          }}
          sticky
        />
        <style>{`
          .missing-record-row { background-color: #fff1f0 !important; }
          .missing-record-row:hover > td { background-color: #ffa39e !important; }
        `}</style>
      </Card>
    </div>
  );
};

export default PayrollReport;
